import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  FormControl,
  InputLabel,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import AmountField from '../../../components/AmountField';

// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

const COMPOUNDING_FREQUENCIES = [
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'yearly', label: 'Yearly' }
];

const INTEREST_PAYOUT_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'yearly', label: 'Annually' },
  { value: 'at_maturity', label: 'At Maturity' }
];

// Update helper function to calculate interest earnings
const calculateInterestEarnings = (amount, rate, frequency, period, compoundingFrequency) => {
  // Calculate effective annual rate based on compounding frequency
  let effectiveRate;
  switch (compoundingFrequency) {
    case 'quarterly':
      effectiveRate = Math.pow(1 + (rate / 400), 4) - 1;
      break;
    case 'half_yearly':
      effectiveRate = Math.pow(1 + (rate / 200), 2) - 1;
      break;
    case 'yearly':
      effectiveRate = rate / 100;
      break;
    default:
      effectiveRate = rate / 100;
  }

  if (frequency === 'at_maturity') {
    // Calculate total interest earned at maturity using effective rate
    const years = period / 365;
    const maturityValue = amount * Math.pow(1 + effectiveRate, years);
    return maturityValue - amount;
  }

  // For periodic payouts, calculate based on effective rate
  const annualInterest = amount * effectiveRate;
  switch (frequency) {
    case 'monthly':
      return annualInterest / 12;
    case 'quarterly':
      return annualInterest / 4;
    case 'half_yearly':
      return annualInterest / 2;
    case 'yearly':
      return annualInterest;
    default:
      return 0;
  }
};

// Update helper function to calculate maturity value
const calculateMaturityValue = (amount, rate, period, frequency, compoundingFrequency) => {
  if (frequency === 'at_maturity') {
    // Calculate maturity value using effective rate based on compounding frequency
    const years = period / 365;
    let effectiveRate;
    
    switch (compoundingFrequency) {
      case 'quarterly':
        return amount * Math.pow(1 + (rate / 400), years * 4);
      case 'half_yearly':
        return amount * Math.pow(1 + (rate / 200), years * 2);
      case 'yearly':
        return amount * Math.pow(1 + (rate / 100), years);
      default:
        return amount * Math.pow(1 + (rate / 100), years);
    }
  }
  return amount; // For other frequencies, return invested amount
};

// Add this style object at the top level of the component
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

const FixedDeposit = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-debt-fd', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [depositTransactions, setDepositTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editInterestRate, setEditInterestRate] = useState('');
  const [editCompoundingFrequency, setEditCompoundingFrequency] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editNotes, setEditNotes] = useState('');
  
  // Form state
  const [depositName, setDepositName] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [amountInvested, setAmountInvested] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [compoundingFrequency, setCompoundingFrequency] = useState('');
  const [notes, setNotes] = useState('');
  
  // User's saved deposit names (will be populated from existing investments)
  const [userDepositNames, setUserDepositNames] = useState([]);

  // Add new state variables for period components
  const [periodYears, setPeriodYears] = useState('');
  const [periodMonths, setPeriodMonths] = useState('');
  const [periodDays, setPeriodDays] = useState('');
  const [editPeriodYears, setEditPeriodYears] = useState('');
  const [editPeriodMonths, setEditPeriodMonths] = useState('');
  const [editPeriodDays, setEditPeriodDays] = useState('');

  // Add back the missing state variables
  const [period, setPeriod] = useState('');
  const [maturityDate, setMaturityDate] = useState(null);
  const [interestPayoutFrequency, setInterestPayoutFrequency] = useState('');
  const [editPeriod, setEditPeriod] = useState('');
  const [editMaturityDate, setEditMaturityDate] = useState(null);
  const [editInterestPayoutFrequency, setEditInterestPayoutFrequency] = useState('');

  // Add function to convert period components to total days
  const convertToTotalDays = (years, months, days) => {
    const totalDays = (parseInt(years || 0) * 365) + (parseInt(months || 0) * 30) + parseInt(days || 0);
    return totalDays;
  };

  // Add function to convert total days to period components
  const convertToPeriodComponents = (totalDays) => {
    const years = Math.floor(totalDays / 365);
    const remainingDays = totalDays % 365;
    const months = Math.floor(remainingDays / 30);
    const days = remainingDays % 30;
    return { years, months, days };
  };

  // Update handlePeriodChange
  const handlePeriodChange = () => {
    const years = parseInt(periodYears) || 0;
    const months = parseInt(periodMonths) || 0;
    const days = parseInt(periodDays) || 0;
    
    const totalDays = (years * 365) + (months * 30) + days;
    setPeriod(totalDays.toString());
    
    if (purchaseDate && (years > 0 || months > 0 || days > 0)) {
      const newMaturityDate = new Date(purchaseDate);
      newMaturityDate.setDate(newMaturityDate.getDate() + totalDays);
      setMaturityDate(newMaturityDate);
    }
  };

  // Update handleMaturityDateChange
  const handleMaturityDateChange = (newDate) => {
    setMaturityDate(newDate);
    if (newDate && purchaseDate) {
      const diffTime = Math.abs(newDate - purchaseDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setPeriod(diffDays.toString());
      
      // Update period components
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const months = Math.floor(remainingDays / 30);
      const days = remainingDays % 30;
      
      setPeriodYears(years.toString());
      setPeriodMonths(months.toString());
      setPeriodDays(days.toString());
    }
  };

  // Update handleEditPeriodChange
  const handleEditPeriodChange = (field, value) => {
    // Update the specific field
    switch (field) {
      case 'years':
        setEditPeriodYears(value);
        break;
      case 'months':
        setEditPeriodMonths(value);
        break;
      case 'days':
        setEditPeriodDays(value);
        break;
    }

    // Get current values for all fields
    const years = field === 'years' ? parseInt(value) || 0 : parseInt(editPeriodYears) || 0;
    const months = field === 'months' ? parseInt(value) || 0 : parseInt(editPeriodMonths) || 0;
    const days = field === 'days' ? parseInt(value) || 0 : parseInt(editPeriodDays) || 0;
    
    // Calculate total days
    const totalDays = (years * 365) + (months * 30) + days;
    setEditPeriod(totalDays.toString());
    
    // Update maturity date if we have a purchase date
    if (editPurchaseDate) {
      const newMaturityDate = new Date(editPurchaseDate);
      newMaturityDate.setDate(newMaturityDate.getDate() + totalDays);
      setEditMaturityDate(newMaturityDate);
    }
  };

  // Update handleEditMaturityDateChange
  const handleEditMaturityDateChange = (newDate) => {
    setEditMaturityDate(newDate);
    if (newDate && editPurchaseDate) {
      const diffTime = Math.abs(newDate - editPurchaseDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setEditPeriod(diffDays.toString());
      
      // Update period components
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const months = Math.floor(remainingDays / 30);
      const days = remainingDays % 30;
      
      setEditPeriodYears(years.toString());
      setEditPeriodMonths(months.toString());
      setEditPeriodDays(days.toString());
    }
  };

  // Update handlePurchaseDateChange
  const handlePurchaseDateChange = (newDate) => {
    setPurchaseDate(newDate);
    if (newDate && maturityDate) {
      const diffTime = Math.abs(maturityDate - newDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setPeriod(diffDays.toString());
      
      // Update period components
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const months = Math.floor(remainingDays / 30);
      const days = remainingDays % 30;
      
      setPeriodYears(years.toString());
      setPeriodMonths(months.toString());
      setPeriodDays(days.toString());
    }
  };

  // Update handleEditPurchaseDateChange
  const handleEditPurchaseDateChange = (newDate) => {
    setEditPurchaseDate(newDate);
    if (newDate && editPeriod) {
      const totalDays = parseInt(editPeriod);
      const newMaturityDate = new Date(newDate);
      newMaturityDate.setDate(newMaturityDate.getDate() + totalDays);
      setEditMaturityDate(newMaturityDate);
    }
  };

  // Dummy data for Fixed Deposits
  const dummyData = [
    { id: 1, name: 'Bank A FD', value: 500000, interestRate: 6.5, compoundingFrequency: 'quarterly', notes: 'High yield FD' },
    { id: 2, name: 'Bank B FD', value: 300000, interestRate: 6.0, compoundingFrequency: 'half_yearly', notes: 'Tax saving FD' },
    { id: 3, name: 'Bank C FD', value: 200000, interestRate: 5.5, compoundingFrequency: 'yearly', notes: 'Regular FD' },
  ];

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch fixed deposit investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Fetching fixed deposit investments for user ID:', user.id);
      
      // Fetch from Supabase
      const { data, error } = await supabase
        .from('fixed_deposit_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        console.error('Error fetching fixed deposit investments:', error);
        throw error;
      }
      
      console.log('Fetched fixed deposit data:', data);
      
      // Process data - format and group by deposit name
      if (data && data.length > 0) {
        // Extract unique deposit names for autocomplete
        const uniqueDepositNames = [...new Set(data.map(item => item.name))];
        setUserDepositNames(uniqueDepositNames);
        
        // Format data for display
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: item.amount_invested,
          interestRate: item.interest_rate,
          compoundingFrequency: item.compounding_frequency,
          period: item.period,
          maturityDate: item.maturity_date,
          interestPayoutFrequency: item.interest_payout_frequency,
          purchaseDate: item.purchase_date,
          notes: item.notes
        }));
        
        // Group investments by deposit name
        const groupedInvestments = groupInvestmentsByDeposit(formattedData);
        setInvestments(groupedInvestments);
        
        // Calculate total value
        const total = groupedInvestments.reduce((sum, deposit) => sum + deposit.value, 0);
        setTotalValue(total);
        console.log('Total fixed deposit value:', total);
      } else {
        console.log('No fixed deposit investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserDepositNames([]);
      }
    } catch (error) {
      console.error('Error processing fixed deposit investments:', error);
      // Fall back to empty state
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  // Update groupInvestmentsByDeposit function to include new fields
  const groupInvestmentsByDeposit = (investments) => {
    const depositMap = new Map();
    
    investments.forEach(investment => {
      const { name, value, interestRate, compoundingFrequency, period, maturityDate, interestPayoutFrequency } = investment;
      
      if (depositMap.has(name)) {
        const deposit = depositMap.get(name);
        deposit.value += value;
        deposit.transactions.push(investment);
      } else {
        depositMap.set(name, {
          name,
          value,
          interestRate,
          compoundingFrequency,
          period,
          maturityDate,
          interestPayoutFrequency,
          transactions: [investment]
        });
      }
    });
    
    // Convert map to array
    return Array.from(depositMap.values());
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  const pieChartData = investments.map((item) => ({
    id: item.id,
    value: item.value,
    label: item.name,
  }));

  // Modal handlers
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  
  const handleReset = () => {
    setDepositName('');
    setPurchaseDate(new Date());
    setAmountInvested('');
    setInterestRate('');
    setCompoundingFrequency('');
    setPeriod('');
    setPeriodYears('');
    setPeriodMonths('');
    setPeriodDays('');
    setMaturityDate(null);
    setInterestPayoutFrequency('');
    setNotes('');
  };
  
  // Transaction history modal handlers
  const handleOpenTransactionHistory = (deposit) => {
    setSelectedDeposit(deposit);
    setDepositTransactions(deposit.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedDeposit(null);
    setDepositTransactions([]);
  };
  
  // Transaction action handlers
  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Edit transaction handlers
  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    setEditAmountInvested(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditInterestRate(currentTransaction.interestRate ? currentTransaction.interestRate.toString() : '');
    setEditCompoundingFrequency(currentTransaction.compoundingFrequency || '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    
    // Convert period to components
    const periodComponents = convertToPeriodComponents(currentTransaction.period || 0);
    setEditPeriodYears(periodComponents.years.toString());
    setEditPeriodMonths(periodComponents.months.toString());
    setEditPeriodDays(periodComponents.days.toString());
    setEditPeriod(currentTransaction.period ? currentTransaction.period.toString() : '');
    
    setEditMaturityDate(currentTransaction.maturityDate ? new Date(currentTransaction.maturityDate) : null);
    setEditInterestPayoutFrequency(currentTransaction.interestPayoutFrequency || '');
    setEditNotes(currentTransaction.notes || '');
    
    setOpenEditModal(true);
  };
  
  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };
  
  // Delete transaction handlers
  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  // Add function to calculate maturity date from period
  const calculateMaturityDate = (startDate, days) => {
    if (!startDate || !days) return null;
    const date = new Date(startDate);
    date.setDate(date.getDate() + parseInt(days));
    return date;
  };

  // Add function to calculate period from maturity date
  const calculatePeriod = (startDate, maturityDate) => {
    if (!startDate || !maturityDate) return '';
    const diffTime = Math.abs(maturityDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays.toString();
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    // Check if at least one period field is filled
    const hasPeriod = periodYears || periodMonths || periodDays;
    if (!depositName || !purchaseDate || !amountInvested || !interestRate || 
        !compoundingFrequency || !interestPayoutFrequency || (!hasPeriod && !maturityDate)) {
      alert('Please fill all required fields');
      return;
    }
    try {
      setLoading(true);
      let depositNameValue = '';
      if (typeof depositName === 'string') {
        depositNameValue = depositName.trim();
      } else if (depositName && typeof depositName === 'object') {
        depositNameValue = depositName.label?.trim() || '';
      }
      if (!depositNameValue) {
        alert('Please enter a valid deposit name');
        setLoading(false);
        return;
      }
      // Check for duplicate deposit name
      const { data: existingDeposits, error: checkError } = await supabase
        .from('fixed_deposit_investments')
        .select('name')
        .eq('user_id', user.id)
        .eq('name', depositNameValue);
      if (checkError) throw checkError;
      if (existingDeposits && existingDeposits.length > 0) {
        alert('A deposit with this name already exists. Please use a different name.');
        setLoading(false);
        return;
      }
      // Calculate total days from period components
      const years = parseInt(periodYears) || 0;
      const months = parseInt(periodMonths) || 0;
      const days = parseInt(periodDays) || 0;
      const totalDays = (years * 365) + (months * 30) + days;
      let finalMaturityDate = null;
      if (hasPeriod) {
        finalMaturityDate = new Date(purchaseDate);
        finalMaturityDate.setDate(finalMaturityDate.getDate() + totalDays);
      } else if (maturityDate) {
        finalMaturityDate = maturityDate;
      }
      const newInvestment = {
        name: depositNameValue,
        purchase_date: purchaseDate.toISOString().split('T')[0],
        amount_invested: parseFloat(amountInvested),
        interest_rate: parseFloat(interestRate),
        compounding_frequency: compoundingFrequency,
        period: totalDays,
        maturity_date: finalMaturityDate ? finalMaturityDate.toISOString().split('T')[0] : null,
        interest_payout_frequency: interestPayoutFrequency,
        notes: notes || null,
        user_id: user.id
      };
      console.log('Creating new fixed deposit investment:', newInvestment);
      const { data, error } = await supabase
        .from('fixed_deposit_investments')
        .insert([newInvestment])
        .select();
      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }
      console.log('Insert response:', data);
      if (data && data.length > 0) {
        await fetchInvestments();
      }
      handleReset();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransaction = async () => {
    // Only require editMaturityDate, not Y M D fields
    if (!editAmountInvested || !editInterestRate || !editCompoundingFrequency || 
        !editPurchaseDate || !editInterestPayoutFrequency || !editMaturityDate) {
      alert('Please fill all required fields');
      return;
    }
    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }
    try {
      setLoading(true);
      // Check for duplicate deposit name only if name is being changed
      if (currentTransaction.name !== currentTransaction.name.trim()) {
        const { data: existingDeposits, error: checkError } = await supabase
          .from('fixed_deposit_investments')
          .select('name')
          .eq('user_id', user.id)
          .eq('name', currentTransaction.name.trim())
          .neq('id', currentTransaction.id); // Exclude current transaction
        if (checkError) throw checkError;
        if (existingDeposits && existingDeposits.length > 0) {
          alert('A deposit with this name already exists. Please use a different name.');
          setLoading(false);
          return;
        }
      }
      // Calculate period from maturity date
      let periodDays = 0;
      if (editMaturityDate && editPurchaseDate) {
        const diffTime = Math.abs(editMaturityDate - editPurchaseDate);
        periodDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }
      const updatedTransaction = {
        name: currentTransaction.name,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        amount_invested: parseFloat(editAmountInvested),
        interest_rate: parseFloat(editInterestRate),
        compounding_frequency: editCompoundingFrequency,
        period: periodDays,
        maturity_date: editMaturityDate ? editMaturityDate.toISOString().split('T')[0] : null,
        interest_payout_frequency: editInterestPayoutFrequency,
        notes: editNotes || null
      };
      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);
      const { data, error } = await supabase
        .from('fixed_deposit_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();
      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }
      console.log('Update response:', data);
      await fetchInvestments();
      if (selectedDeposit && openHistoryModal) {
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('fixed_deposit_investments')
          .select('*')
          .eq('name', selectedDeposit.name)
          .order('purchase_date', { ascending: false });
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            value: item.amount_invested,
            interestRate: item.interest_rate,
            compoundingFrequency: item.compounding_frequency,
            period: item.period,
            maturityDate: item.maturity_date,
            interestPayoutFrequency: item.interest_payout_frequency,
            purchaseDate: item.purchase_date,
            notes: item.notes
          }));
          setDepositTransactions(formattedTransactions);
        }
      }
      handleEditModalClose();
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('fixed_deposit_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedDeposit && openHistoryModal) {
        // Get the latest transactions for this deposit
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('fixed_deposit_investments')
          .select('*')
          .eq('name', selectedDeposit.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              value: item.amount_invested,
              interestRate: item.interest_rate,
              compoundingFrequency: item.compounding_frequency,
              purchaseDate: item.purchase_date,
              notes: item.notes
            }));
            
            setDepositTransactions(formattedTransactions);
          } else {
            // No transactions left for this deposit, close the modal
            handleCloseHistoryModal();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });

    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'interestRate') {
        return direction === 'asc' 
          ? a.interestRate - b.interestRate
          : b.interestRate - a.interestRate;
      } else if (key === 'maturityDate') {
        const dateA = new Date(a.maturityDate);
        const dateB = new Date(b.maturityDate);
        return direction === 'asc' 
          ? dateA - dateB
          : dateB - dateA;
      } else if (key === 'tenure') {
        const tenureA = calculatePeriod(new Date(a.purchaseDate), new Date(a.maturityDate));
        const tenureB = calculatePeriod(new Date(b.purchaseDate), new Date(b.maturityDate));
        return direction === 'asc' 
          ? tenureA - tenureB
          : tenureB - tenureA;
      } else if (key === 'maturityValue') {
        const valueA = calculateMaturityValue(
          a.value,
          a.interestRate,
          a.period,
          a.interestPayoutFrequency,
          a.compoundingFrequency
        );
        const valueB = calculateMaturityValue(
          b.value,
          b.interestRate,
          b.period,
          b.interestPayoutFrequency,
          b.compoundingFrequency
        );
        return direction === 'asc' 
          ? valueA - valueB
          : valueB - valueA;
      } else if (key === 'allocation') {
        const aAllocation = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
        const bAllocation = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      }
      return 0;
    });

    setInvestments(sortedData);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/investments/debt')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-tour="inv-prod-summary">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Fixed Deposits
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Total: {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                maximumFractionDigits: 0,
              }).format(totalValue)}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={fetchInvestments} 
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
              }}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      {/* Modal for adding new investment */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Fixed Deposit Investment
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box component="form" noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
              <TextField
                label="Deposit Name"
                value={depositName}
                onChange={(e) => setDepositName(e.target.value)}
                required
                fullWidth
                placeholder="Enter deposit name"
                helperText="Enter a name for this fixed deposit"
              />
                </Grid>
                <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date of Deposit"
                value={purchaseDate}
                onChange={handlePurchaseDateChange}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
                </Grid>
                <Grid item xs={12} sm={6}>
              <AmountField
                label="Amount Invested"
                value={amountInvested}
                onChange={(e) => setAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "1", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
                </Grid>
                <Grid item xs={12} sm={6}>
              <TextField
                label="Interest Rate (%)"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
                </Grid>
                <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Compounding Frequency</InputLabel>
                <Select
                  value={compoundingFrequency}
                  onChange={(e) => setCompoundingFrequency(e.target.value)}
                  label="Compounding Frequency"
                >
                  {COMPOUNDING_FREQUENCIES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Interest Payout Frequency</InputLabel>
                    <Select
                      value={interestPayoutFrequency}
                      onChange={(e) => setInterestPayoutFrequency(e.target.value)}
                      label="Interest Payout Frequency"
                    >
                      {INTEREST_PAYOUT_FREQUENCIES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                    <TextField
                      label="Years"
                      value={periodYears}
                      onChange={(e) => setPeriodYears(e.target.value)}
                      fullWidth
                      type="number"
                      inputProps={{ step: "1", min: "0" }}
                      helperText="Years"
                      sx={numberInputStyle}
                    />
                    <TextField
                      label="Months"
                      value={periodMonths}
                      onChange={(e) => setPeriodMonths(e.target.value)}
                      fullWidth
                      type="number"
                      inputProps={{ step: "1", min: "0", max: "11" }}
                      helperText="Months (0-11)"
                      sx={numberInputStyle}
                    />
                    <TextField
                      label="Days"
                      value={periodDays}
                      onChange={(e) => setPeriodDays(e.target.value)}
                      fullWidth
                      type="number"
                      inputProps={{ step: "1", min: "0", max: "30" }}
                      helperText="Days (0-30)"
                      sx={numberInputStyle}
                    />
                  </Box>
                </Grid>
                {/* Only show maturity date picker if all period fields are empty */}
                {!(periodYears || periodMonths || periodDays) && (
                  <Grid item xs={12}>
                    <DatePicker
                      label="Maturity Date"
                      value={maturityDate}
                      onChange={handleMaturityDateChange}
                      minDate={purchaseDate}
                      slotProps={{
                        textField: { 
                          fullWidth: true,
                          helperText: "Or select maturity date"
                        }
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={1}
                    fullWidth
                    placeholder="Additional details about this investment (optional)"
                    sx={{ minHeight: 0, maxHeight: 40 }}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Transaction Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Transaction Modal */}
      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box component="form" noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
              <Typography variant="subtitle1">
                Deposit: {currentTransaction?.name}
              </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date of Deposit"
                value={editPurchaseDate}
                onChange={handleEditPurchaseDateChange}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
                </Grid>
                <Grid item xs={12} sm={6}>
              <AmountField
                label="Amount Invested"
                value={editAmountInvested}
                onChange={(e) => setEditAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "1", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
                </Grid>
                <Grid item xs={12} sm={6}>
              <TextField
                label="Interest Rate (%)"
                value={editInterestRate}
                onChange={(e) => setEditInterestRate(e.target.value)}
                required
                fullWidth
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
                </Grid>
                <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Compounding Frequency</InputLabel>
                <Select
                  value={editCompoundingFrequency}
                  onChange={(e) => setEditCompoundingFrequency(e.target.value)}
                  label="Compounding Frequency"
                >
                  {COMPOUNDING_FREQUENCIES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Interest Payout Frequency</InputLabel>
                    <Select
                      value={editInterestPayoutFrequency}
                      onChange={(e) => setEditInterestPayoutFrequency(e.target.value)}
                      label="Interest Payout Frequency"
                    >
                      {INTEREST_PAYOUT_FREQUENCIES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {/* Only show the Maturity Date picker for editing, hide Y M D fields */}
                  <DatePicker
                    label="Maturity Date"
                    value={editMaturityDate}
                    onChange={handleEditMaturityDateChange}
                    minDate={editPurchaseDate}
                    slotProps={{
                      textField: { 
                        fullWidth: true,
                        helperText: "Select or edit maturity date"
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
              <TextField
                label="Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Additional details about this investment (optional)"
              />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Deposit: {currentTransaction.name}<br />
                Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentTransaction.value)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">No Fixed Deposits Found</Typography>
          <Typography variant="body2">
            No fixed deposit investments found. Start investing to see them here.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Deposit Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Deposit Name
                        {sortConfig.key === 'name' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('value')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Value
                        {sortConfig.key === 'value' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('interestRate')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Interest Rate
                        {sortConfig.key === 'interestRate' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('tenure')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Tenure
                        {sortConfig.key === 'tenure' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('maturityDate')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Maturity Date
                        {sortConfig.key === 'maturityDate' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell
                        data-tour="inv-fd-maturity"
                        align="right" 
                        onClick={() => handleSort('maturityValue')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Maturity Value
                        {sortConfig.key === 'maturityValue' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Allocation
                        {sortConfig.key === 'allocation' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => {
                      const maturityValue = calculateMaturityValue(
                        investment.value,
                        investment.interestRate,
                        investment.period,
                        investment.interestPayoutFrequency,
                        investment.compoundingFrequency
                      );

                      // Calculate years and months from period
                      const years = Math.floor(investment.period / 365);
                      const remainingDays = investment.period % 365;
                      const months = Math.floor(remainingDays / 30);

                      return (
                        <TableRow
                          key={investment.id}
                          onClick={() => handleOpenTransactionHistory(investment)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: COLORS[index % COLORS.length],
                                  mr: 2,
                                }}
                              />
                              {investment.name}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(investment.value)}
                          </TableCell>
                          <TableCell align="right">
                            {investment.interestRate}%
                          </TableCell>
                          <TableCell align="right">
                            {(() => {
                              const period = investment.period || 0;
                              const y = Math.floor(period / 365);
                              const remDays = period % 365;
                              const m = Math.floor(remDays / 30);
                              const d = remDays % 30;
                              if (period === 0) return '-';
                              let str = '';
                              if (y > 0) str += `${y}Y `;
                              if (m > 0) str += `${m}M `;
                              if (d > 0) str += `${d}D`;
                              if (!y && !m && !d) str = '< 1D';
                              return str.trim();
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            {investment.maturityDate 
                              ? new Date(investment.maturityDate).toLocaleDateString('en-IN')
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(maturityValue)}
                          </TableCell>
                          <TableCell align="right">
                            {((investment.value / totalValue) * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View transactions">
                              <IconButton size="small" sx={{ color: 'primary.main' }}>
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(investments.reduce((sum, inv) => 
                          sum + calculateMaturityValue(
                            inv.value,
                            inv.interestRate,
                            inv.period,
                            inv.interestPayoutFrequency,
                            inv.compoundingFrequency
                          ), 0
                        ))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Paper
              sx={{
                height: '100%',
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Distribution
                </Typography>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                {pieChartData.length > 0 && (
                  <PieChart colors={chartPalette}
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={450}
                    legend={{
                      hidden: true
                    }}
                    margin={{ top: 10, bottom: 50, left: 20, right: 20 }}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'bottom', horizontal: 'middle' },
                        padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        itemMarkWidth: 8,
                        itemMarkHeight: 8,
                        markGap: 5,
                        itemGap: 15,
                        sx: {
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          gap: 2,
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Transaction History Modal */}
      <Modal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {selectedDeposit?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Value: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(selectedDeposit?.value || 0)}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseHistoryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Interest Rate (%)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Tenure (Y M)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Maturity Date</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Interest Earnings</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Maturity Value</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Notes</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {depositTransactions.length > 0 ? (
                  depositTransactions.map((transaction) => {
                    const maturityValue = calculateMaturityValue(
                      transaction.value,
                      transaction.interestRate,
                      transaction.period,
                      transaction.interestPayoutFrequency,
                      transaction.compoundingFrequency
                    );
                    const interestEarnings = maturityValue - transaction.value;
                    // Calculate years and months from period
                    const years = Math.floor((transaction.period || 0) / 365);
                    const remainingDays = (transaction.period || 0) % 365;
                    const months = Math.floor(remainingDays / 30);
                    return (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {transaction.purchaseDate 
                            ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(transaction.value)}
                        </TableCell>
                        <TableCell align="right">
                          {transaction.interestRate 
                            ? parseFloat(transaction.interestRate).toFixed(2)
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const period = transaction.period || 0;
                            const y = Math.floor(period / 365);
                            const remDays = period % 365;
                            const m = Math.floor(remDays / 30);
                            const d = remDays % 30;
                            if (period === 0) return '-';
                            let str = '';
                            if (y > 0) str += `${y}Y `;
                            if (m > 0) str += `${m}M `;
                            if (d > 0) str += `${d}D`;
                            if (!y && !m && !d) str = '< 1D';
                            return str.trim();
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {transaction.maturityDate 
                            ? new Date(transaction.maturityDate).toLocaleDateString('en-IN')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            {(() => {
                              if (transaction.interestPayoutFrequency === 'at_maturity') {
                                const earnings = calculateInterestEarnings(
                                  transaction.value,
                                  transaction.interestRate,
                                  transaction.interestPayoutFrequency,
                                  transaction.period,
                                  transaction.compoundingFrequency
                                );
                                return (
                                  <>
                                    <Typography>
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                                      }).format(earnings)}
                          </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      total at maturity<br />
                                      Compounding: {COMPOUNDING_FREQUENCIES.find(f => f.value === transaction.compoundingFrequency)?.label.toLowerCase() || '-'}
                                    </Typography>
                                  </>
                                );
                              } else {
                                // Calculate number of periods and per-period earnings
                                let periods = 1;
                                let periodLabel = '';
                                switch (transaction.interestPayoutFrequency) {
                                  case 'monthly':
                                    periods = Math.round((transaction.period || 0) / 30);
                                    periodLabel = 'M';
                                    break;
                                  case 'quarterly':
                                    periods = Math.round((transaction.period || 0) / 91.25);
                                    periodLabel = 'Q';
                                    break;
                                  case 'half_yearly':
                                    periods = Math.round((transaction.period || 0) / 182.5);
                                    periodLabel = 'H';
                                    break;
                                  case 'yearly':
                                    periods = Math.round((transaction.period || 0) / 365);
                                    periodLabel = 'Y';
                                    break;
                                }
                                const perPeriod = calculateInterestEarnings(
                                  transaction.value,
                                  transaction.interestRate,
                                  transaction.interestPayoutFrequency,
                                  transaction.period,
                                  transaction.compoundingFrequency
                                );
                                const total = perPeriod * periods;
                                return (
                                  <>
                                    <Typography>
                                      {new Intl.NumberFormat('en-IN', {
                                        style: 'currency',
                                        currency: 'INR',
                                        maximumFractionDigits: 0,
                                      }).format(perPeriod)}
                                      <span style={{ fontSize: 13, color: '#888', marginLeft: 4 }}>
                                        /{periodLabel}
                                      </span>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      total: {new Intl.NumberFormat('en-IN', {
                                        style: 'currency',
                                        currency: 'INR',
                                        maximumFractionDigits: 0,
                                      }).format(total)} (over tenure)
                                      <br />
                                      Compounding: {COMPOUNDING_FREQUENCIES.find(f => f.value === transaction.compoundingFrequency)?.label.toLowerCase() || '-'}
                                    </Typography>
                                  </>
                                );
                              }
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(maturityValue)}
                        </TableCell>
                        <TableCell>
                          {transaction.notes || '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {depositTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(depositTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                    </TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(depositTransactions.reduce((sum, tx) => {
                        const maturityValue = calculateMaturityValue(
                          tx.value,
                          tx.interestRate,
                          tx.period,
                          tx.interestPayoutFrequency,
                          tx.compoundingFrequency
                        );
                        return sum + (maturityValue - tx.value);
                      }, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(depositTransactions.reduce((sum, tx) => {
                        const maturityValue = calculateMaturityValue(
                          tx.value,
                          tx.interestRate,
                          tx.period,
                          tx.interestPayoutFrequency,
                          tx.compoundingFrequency
                        );
                        return sum + maturityValue;
                      }, 0))}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleCloseHistoryModal}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <GuidedTour steps={TOURS['inv-debt-fd']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default FixedDeposit;